// Galaxy.tsx — The one continuous map: every star is always rendered (as a
// floored, textured sun), and the focused star additionally shows its planetary
// system. Clicking just flies the camera; nothing here is repositioned.
import { useGame } from '../state/context';
import type { Galaxy as GalaxyData } from '../types';
import { StarMarker } from './StarMarker';
import { SystemDetail } from './SystemDetail';
import { GalacticCore } from './GalacticCore';
import { GalaxyDust } from './GalaxyDust';
import { Nebulae } from './Nebulae';
import { Hyperlanes } from './Hyperlanes';
import { ShipMarkers } from './ShipMarkers';
import { Territory } from './Territory';

interface Props {
  galaxy: GalaxyData;
}

export function Galaxy({ galaxy }: Props) {
  const { focusedSystemId } = useGame();
  const focused = focusedSystemId
    ? galaxy.systems.find((s) => s.id === focusedSystemId) ?? null
    : null;

  return (
    <group>
      {/* Luminous galaxy backdrop (core bulge, arm nebulae, dust) + the hyperlane
          network; all fade out once a system is focused so they don't wash over
          the close-up scene. */}
      {focused ? null : (
        <>
          <GalacticCore />
          <Nebulae />
          <GalaxyDust />
          <Hyperlanes galaxy={galaxy} />
          {/* Ship icons live at the galaxy level only; hidden once a system is focused
              (its in-system 3D ships render in SystemDetail instead). */}
          <ShipMarkers galaxy={galaxy} />
          <Territory galaxy={galaxy} />
        </>
      )}
      {galaxy.systems.map((sys) => (
        <StarMarker key={sys.id} system={sys} />
      ))}
      {focused ? <SystemDetail galaxy={galaxy} system={focused} /> : null}
    </group>
  );
}
